<template>
  <div class="page">
    <h2>理疗活动管理</h2>
    <div class="toolbar">
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="DRAFT">草稿</option>
        <option value="PUBLISHED">已发布</option>
        <option value="OFFLINE">已下架</option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <button :disabled="loading" @click="openCreate">新增活动</button>
      <button :disabled="loading" @click="openCategoryDialog">分类管理</button>
    </div>
    <table>
      <thead>
        <tr><th>ID</th><th>名称</th><th>分类</th><th>时长</th><th>价格</th><th>等级/规格价格</th><th>优先级</th><th>状态</th><th>操作</th></tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.name }}</td>
          <td>
            <span v-for="(cat, idx) in (item.categories || [])" :key="cat.id" class="category-tag">{{ cat.name }}<template v-if="idx < item.categories.length - 1">、</template></span>
            <span v-if="!item.categories || !item.categories.length">-</span>
          </td>
          <td>{{ item.durationMinutes }}分钟</td>
          <td>¥{{ item.price }}</td>
          <td>
            <template v-if="item.hasDisplayableSkus && item.skus && item.skus.length">
              <span v-for="sku in item.skus.filter(s => s.enabled !== false)" :key="sku.id || sku.name" class="grade-price-tag">{{ sku.name }} ¥{{ sku.price }}</span>
            </template>
            <template v-else-if="item.gradePrices && item.gradePrices.length">
              <span v-for="gp in item.gradePrices" :key="gp.grade" class="grade-price-tag">{{ gp.grade }} +{{ gp.price ?? 0 }}</span>
            </template>
            <span v-else>-</span>
          </td>
          <td>{{ item.sortOrder ?? 0 }}</td>
          <td>
            <span :class="['status-badge', 'status-' + item.status.toLowerCase()]">
              {{ statusMap[item.status] || item.status }}
            </span>
          </td>
          <td>
            <button :disabled="loading" @click="openEdit(item)">编辑</button>
            <button :disabled="loading || item.status==='PUBLISHED'" @click="changeStatus(item, 'PUBLISHED')">发布</button>
            <button :disabled="loading || item.status==='OFFLINE'" @click="changeStatus(item, 'OFFLINE')">下架</button>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="pagination">
      <button :disabled="loading || page===0" @click="changePage(page-1)">上一页</button>
      <span>{{ page + 1 }}</span>
      <button :disabled="loading || page+1>=totalPages" @click="changePage(page+1)">下一页</button>
    </div>

    <div v-if="showDialog" class="dialog-mask">
      <div class="dialog dialog-wide">
        <h3>{{ editingId ? '编辑活动' : '新增活动' }}</h3>

        <!-- 分区一：基本信息 -->
        <fieldset class="section-fieldset">
          <legend>基本信息</legend>
          <div class="form-group">
            <label class="field-block" for="activity-name">
              <span class="field-label">活动名称</span>
              <span class="field-hint">对外展示的名称，保存前必填。</span>
              <input id="activity-name" v-model="form.name" type="text" placeholder="例如：肩颈理疗体验" />
            </label>
          </div>
          <div class="form-row">
            <div class="form-group form-col">
              <label class="field-block" for="activity-duration">
                <span class="field-label">时长（分钟）</span>
                <span class="field-hint" style="visibility: hidden">无等级或未选等级时的价格</span>
                <input id="activity-duration" v-model.number="form.durationMinutes" type="number" min="1" step="1" placeholder="60" />
              </label>
            </div>
            <div class="form-group form-col">
              <label class="field-block" for="activity-price">
                <span class="field-label">默认价格（元）</span>
                <span class="field-hint">无等级或未选等级时的价格</span>
                <input id="activity-price" v-model.number="form.price" type="number" min="0" step="0.01" placeholder="0" />
              </label>
            </div>
            <div class="form-group form-col">
              <label class="field-block" for="activity-original-price">
                <span class="field-label">默认原价（元）</span>
                <span class="field-hint" style="visibility: hidden">无等级或未选等级时的价格</span>
                <input id="activity-original-price" v-model.number="form.originalPrice" type="number" min="0" step="0.01" placeholder="0" />
              </label>
            </div>
          </div>
          <div class="form-group">
            <label class="field-block" for="activity-sort-order">
              <span class="field-label">优先级</span>
              <span class="field-hint">数值越小越靠前，0 为最高优先级</span>
              <input id="activity-sort-order" v-model.number="form.sortOrder" type="number" min="0" step="1" placeholder="0" />
            </label>
          </div>
          <div class="form-group">
            <label class="field-block" for="activity-summary">
              <span class="field-label">简介</span>
              <input id="activity-summary" v-model="form.summary" type="text" placeholder="可选" />
            </label>
          </div>
          <div class="form-group">
            <label class="field-block">
              <span class="field-label">分类（可多选）</span>
              <div class="category-checklist">
                <label v-for="cat in categoryOptions" :key="cat.id" class="category-check-label">
                  <input type="checkbox" :value="cat.id" v-model="form.categoryIds" />
                  {{ cat.name }}
                </label>
              </div>
              <div v-if="categoryOptions.length === 0" class="cs-empty">暂无分类，请先在分类管理中添加</div>
            </label>
          </div>
          <div class="form-group">
            <label class="field-block" for="activity-address">
              <span class="field-label">服务地点</span>
              <input id="activity-address" v-model="form.serviceAddress" type="text" placeholder="例如：XX 路 XX 号 XX 店" />
            </label>
          </div>
        </fieldset>

        <!-- 分区二：内容板块 -->
        <fieldset class="section-fieldset">
          <legend>内容板块</legend>
          <div class="cs-toolbar">
            <span class="cs-hint">添加不同类型的内容卡片，在小程序详情页按顺序渲染展示。</span>
            <div class="cs-add-btns">
              <button type="button" class="ghost-button cs-add-btn" @click="addSection('text')">+ 纯文本</button>
              <button type="button" class="ghost-button cs-add-btn" @click="addSection('numberedList')">+ 编号列表</button>
              <button type="button" class="ghost-button cs-add-btn" @click="addSection('bulletList')">+ 圆点列表</button>
              <button type="button" class="ghost-button cs-add-btn" @click="addSection('highlight')">+ 重点提示</button>
            </div>
          </div>
          <div v-if="!form.contentSections || !form.contentSections.length" class="cs-empty">暂无内容板块，点击上方按钮添加。</div>
          <div v-for="(section, sIdx) in form.contentSections" :key="sIdx" class="cs-card">
            <div class="cs-card-header">
              <input v-model="section.title" class="cs-title-input" placeholder="板块标题" />
              <span class="cs-type-badge">{{ sectionTypeLabels[section.type] || section.type }}</span>
              <div class="cs-card-actions">
                <button v-if="sIdx > 0" type="button" class="cs-action-btn" @click="moveSection(sIdx, -1)" title="上移">&#8593;</button>
                <button v-if="sIdx < form.contentSections.length - 1" type="button" class="cs-action-btn" @click="moveSection(sIdx, 1)" title="下移">&#8595;</button>
                <button type="button" class="cs-action-btn cs-remove-btn" @click="removeSection(sIdx)" title="删除">&#10005;</button>
              </div>
            </div>

            <!-- text 类型 -->
            <div v-if="section.type === 'text'" class="cs-card-body">
              <textarea v-model="section.content" class="cs-textarea" placeholder="输入纯文本内容…" rows="4"></textarea>
            </div>

            <!-- numberedList 类型 -->
            <div v-if="section.type === 'numberedList'" class="cs-card-body">
              <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                <span class="cs-item-num">{{ iIdx + 1 }}</span>
                <input v-model="item.title" class="cs-item-title" placeholder="条目标题（可选）" />
                <input v-model="item.description" class="cs-item-desc" placeholder="条目描述" />
                <button type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
              </div>
              <button type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
            </div>

            <!-- bulletList 类型 -->
            <div v-if="section.type === 'bulletList'" class="cs-card-body">
              <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                <span class="cs-item-bullet">&#8226;</span>
                <input v-model="item.title" class="cs-item-desc" placeholder="关键词或短语" />
                <button type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
              </div>
              <button type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
            </div>

            <!-- highlight 类型 -->
            <div v-if="section.type === 'highlight'" class="cs-card-body cs-highlight-body">
              <div v-for="(item, iIdx) in section.items" :key="iIdx" class="cs-item-row">
                <span class="cs-item-num">{{ iIdx + 1 }}</span>
                <input v-model="item.title" class="cs-item-title" placeholder="条目标题（可选）" />
                <input v-model="item.description" class="cs-item-desc" placeholder="条目描述" />
                <button type="button" class="cs-action-btn cs-remove-btn" @click="section.items.splice(iIdx, 1)">&#10005;</button>
              </div>
              <button type="button" class="ghost-button" @click="section.items.push({ title: '', description: '' })">+ 添加条目</button>
            </div>
          </div>
        </fieldset>

        <!-- 分区三：理疗师等级差价 -->
        <fieldset class="section-fieldset">
          <legend>理疗师等级差价</legend>
          <p class="field-hint block-hint">等级差价在项目级统一配置，每次预约只加一次。第一项为基准等级，差价固定为 0。</p>
          <div class="form-group">
            <label class="field-block" for="activity-grade-options">
              <span class="field-label">等级选项</span>
              <span class="field-hint">输入等级名称，按回车或逗号添加；第一项为基准等级。</span>
              <input
                id="activity-grade-options"
                v-model="gradeInputText"
                type="text"
                placeholder="输入等级后按回车添加，如：初级"
                @keydown.enter.prevent="addGradeFromInput"
              />
              <div v-if="gradeRows.length > 0" class="grade-chips">
                <span v-for="(row, idx) in gradeRows" :key="idx" class="grade-chip">
                  {{ row.grade }}
                  <button class="chip-remove" @click="removeGradeRow(idx)">×</button>
                </span>
              </div>
            </label>
          </div>
          <div v-if="gradeRows.length > 0" class="grade-price-table-wrap">
            <table class="grade-price-table">
              <thead>
                <tr><th>等级</th><th>等级差价（元）</th><th>原价（元）</th></tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in gradeRows" :key="idx">
                  <td>{{ row.grade }}{{ idx === 0 ? '（基准）' : '' }}</td>
                  <td>
                    <input
                      v-if="idx === 0"
                      type="number"
                      value="0"
                      disabled
                      class="gp-input"
                    />
                    <input
                      v-else
                      v-model.number="row.price"
                      type="number"
                      min="0"
                      step="0.01"
                      class="gp-input"
                    />
                  </td>
                  <td><input v-model.number="row.originalPrice" type="number" min="0" step="0.01" class="gp-input" :disabled="idx === 0" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </fieldset>

        <!-- 分区：部位规格（可选） -->
        <fieldset v-if="skuModeEnabled" class="section-fieldset">
          <legend>部位规格</legend>
          <p class="field-hint block-hint">配置各部位基准价（基准等级下的单价）。多选部位时按基准价累加，再叠加一次项目等级差价。</p>
          <div class="sku-toolbar">
            <button type="button" class="ghost-button" @click="addSkuRow">+ 添加部位</button>
            <button v-if="!skuRows.length" type="button" class="danger-link" @click="disableSkuMode">关闭部位规格</button>
          </div>
          <div v-if="skuRows.length" class="sku-table-wrap">
            <table class="grade-price-table sku-table">
              <thead>
                <tr>
                  <th>部位名称</th>
                  <th>基准价（元）</th>
                  <th>原价（元）</th>
                  <th>时长（分）</th>
                  <th>排序</th>
                  <th>启用</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in skuRows" :key="idx">
                  <td><input v-model="row.name" type="text" class="gp-input" placeholder="如：颈椎" /></td>
                  <td><input v-model.number="row.price" type="number" min="0" step="0.01" class="gp-input" /></td>
                  <td><input v-model.number="row.originalPrice" type="number" min="0" step="0.01" class="gp-input" /></td>
                  <td><input v-model.number="row.durationMinutes" type="number" min="1" step="1" class="gp-input" placeholder="继承" /></td>
                  <td><input v-model.number="row.sortOrder" type="number" min="0" step="1" class="gp-input" /></td>
                  <td><input v-model="row.enabled" type="checkbox" /></td>
                  <td><button type="button" class="cs-action-btn cs-remove-btn" @click="removeSkuRow(idx)">×</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </fieldset>
        <div v-else class="sku-enable-bar">
          <button type="button" class="ghost-button" @click="enableSkuMode">配置部位规格（可选）</button>
        </div>

        <!-- 分区四：图片上传 -->
        <fieldset class="section-fieldset">
          <legend>图片上传</legend>
          <p class="image-spec-banner">以下尺寸按用户端 H5 页面展示效果制定，比例一致可避免裁切变形。支持 JPG / PNG / WebP，单张建议不超过 2MB。</p>
          <div class="form-group">
            <label class="full-width">
              <span class="field-label">封面图片</span>
              <span class="field-hint block-hint">推荐 <strong>750×480</strong> 像素（比例 5:3）。用于项目列表卡片与详情页顶部主图，页面以 cover 居中裁剪展示。</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="coverImageInput"
                  accept="image/*" 
                  @change="e => uploadImage(e, 'coverImageUrl')"
                  style="display: none"
                />
                <div class="image-preview-container">
                  <img 
                    v-if="form.coverImageUrl" 
                    :src="getPreviewUrl(form.coverImageUrl)" 
                    alt="封面图片预览" 
                    class="image-preview"
                  />
                  <div v-else class="image-placeholder">暂无图片</div>
                </div>
                <div class="image-upload-actions">
                  <button type="button" class="ghost-button" @click="$refs.coverImageInput.click()">
                    {{ form.coverImageUrl ? '更换图片' : '选择图片' }}
                  </button>
                  <button v-if="form.coverImageUrl" type="button" class="danger-link" @click.prevent="form.coverImageUrl = ''">清除</button>
                  <span v-if="loadingField === 'coverImageUrl'" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
          </div>

          <div class="form-group">
            <label class="full-width">
              <span class="field-label">轮播图（支持多图）</span>
              <span class="field-hint block-hint">推荐每张 <strong>750×480</strong> 像素（比例 5:3），与封面同规格。详情页顶部横向轮播，按上传顺序展示。</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="carouselImagesInput"
                  accept="image/*" 
                  multiple
                  @change="e => uploadMultipleImages(e, 'carouselImages')"
                  style="display: none"
                />
                <div class="image-preview-grid" v-if="form.carouselImages && form.carouselImages.length">
                  <div v-for="(img, index) in form.carouselImages" :key="index" class="image-preview-container grid-item">
                    <img :src="getPreviewUrl(img)" class="image-preview" />
                    <button class="remove-btn" title="删除" @click.prevent="form.carouselImages.splice(index, 1)">×</button>
                  </div>
                </div>
                <div v-else class="image-preview-container">
                  <div class="image-placeholder">暂无图片</div>
                </div>
                <div class="image-upload-actions">
                  <button type="button" class="ghost-button" @click="$refs.carouselImagesInput.click()">添加图片</button>
                  <button v-if="form.carouselImages && form.carouselImages.length" type="button" class="danger-link" @click.prevent="form.carouselImages = []">清空</button>
                  <span v-if="loadingField === 'carouselImages'" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
          </div>

          <div class="form-group">
            <label class="full-width">
              <span class="field-label">详情图（支持多图）</span>
              <span class="field-hint block-hint">推荐宽度 <strong>750</strong> 像素，高度不限（建议竖版长图）。详情页全宽展示，适合项目介绍、流程示意等说明内容。</span>
              <div class="image-upload-wrapper">
                <input 
                  type="file" 
                  ref="detailImagesInput"
                  accept="image/*" 
                  multiple
                  @change="e => uploadMultipleImages(e, 'detailImages')"
                  style="display: none"
                />
                <div class="image-preview-grid" v-if="form.detailImages && form.detailImages.length">
                  <div v-for="(img, index) in form.detailImages" :key="index" class="image-preview-container grid-item">
                    <img :src="getPreviewUrl(img)" class="image-preview" />
                    <button class="remove-btn" title="删除" @click.prevent="form.detailImages.splice(index, 1)">×</button>
                  </div>
                </div>
                <div v-else class="image-preview-container">
                  <div class="image-placeholder">暂无图片</div>
                </div>
                <div class="image-upload-actions">
                  <button type="button" class="ghost-button" @click="$refs.detailImagesInput.click()">添加图片</button>
                  <button v-if="form.detailImages && form.detailImages.length" type="button" class="danger-link" @click.prevent="form.detailImages = []">清空</button>
                  <span v-if="loadingField === 'detailImages'" class="upload-status">上传中...</span>
                </div>
              </div>
            </label>
          </div>
        </fieldset>

        <div class="actions">
          <button :disabled="loading" @click="showDialog=false">取消</button>
          <button :disabled="loading" @click="save">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showCategoryDialog" class="dialog-mask">
      <div class="dialog dialog-wide">
        <h3>分类管理</h3>
        <div class="category-toolbar">
          <button :disabled="loading" @click="openCreateCategory">新增分类</button>
        </div>
        <table class="category-table">
          <thead>
            <tr><th>ID</th><th>分类名称</th><th>排序</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr v-for="cat in categoryList" :key="cat.id">
              <td>{{ cat.id }}</td>
              <td>{{ cat.name }}</td>
              <td>{{ cat.sortOrder }}</td>
              <td><span :class="['status-badge', 'status-' + cat.status.toLowerCase()]">{{ cat.status === 'ACTIVE' ? '启用' : '停用' }}</span></td>
              <td>
                <button :disabled="loading" @click="openEditCategory(cat)">编辑</button>
                <button :disabled="loading" @click="toggleCategoryStatus(cat)">{{ cat.status === 'ACTIVE' ? '禁用' : '启用' }}</button>
              </td>
            </tr>
            <tr v-if="!categoryList.length"><td colspan="5" style="text-align:center;color:#999">暂无分类</td></tr>
          </tbody>
        </table>

        <div v-if="showCategoryEditDialog" class="dialog-mask">
          <div class="dialog">
            <h3>{{ editingCategoryId ? '编辑分类' : '新增分类' }}</h3>
            <label class="field-block">分类名称
              <input v-model="categoryForm.name" placeholder="例如：核心康养" />
            </label>
            <label class="field-block">排序（越小越靠前）
              <input v-model.number="categoryForm.sortOrder" type="number" placeholder="0" />
            </label>
            <div class="actions">
              <button :disabled="loading" @click="showCategoryEditDialog=false">取消</button>
              <button :disabled="loading" @click="saveCategory">保存</button>
            </div>
          </div>
        </div>

        <div class="actions">
          <button @click="showCategoryDialog=false">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="showDeactivateModal" class="dialog-mask" @click="closeDeactivateModal">
      <div class="dialog deactivate-dialog" @click.stop>
        <h3>{{ deactivateModalMode === 'blocked' ? '无法下架' : '确认下架活动' }}</h3>
        <div v-if="deactivateTarget" class="deactivate-summary">
          <p><strong>活动：</strong>{{ deactivateTarget.item.name }}</p>
        </div>
        <div v-if="deactivateModalMode === 'blocked'" class="deactivate-alert deactivate-alert-danger">
          <p><strong>该活动仍有 {{ deactivateCheck?.activeBookingCount || 0 }} 笔未完成预约，无法下架。</strong></p>
          <p v-if="deactivateCheck?.statusBreakdown && Object.keys(deactivateCheck.statusBreakdown).length">
            预约分布：{{ formatBookingStatusBreakdown(deactivateCheck.statusBreakdown) }}
          </p>
          <p class="deactivate-hint">请先处理完相关预约（完成、取消或改期）后再尝试下架。</p>
        </div>
        <div v-else class="deactivate-alert deactivate-alert-warning">
          <p><strong>请慎重下架！</strong></p>
          <p>下架后 C 端将不再展示该活动，用户无法发起新预约，但历史预约数据仍会保留。</p>
          <p>当前暂无未完成预约，确认要继续下架吗？</p>
        </div>
        <div class="actions">
          <button type="button" class="dialog-cancel-btn" @click="closeDeactivateModal">
            {{ deactivateModalMode === 'blocked' ? '知道了' : '取消' }}
          </button>
          <button
            v-if="deactivateModalMode === 'confirm'"
            type="button"
            class="deactivate-confirm-btn"
            :disabled="loading"
            @click="confirmDeactivate"
          >
            {{ loading ? '处理中...' : '确认下架' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyActivityService } from '@/services/therapyActivityService'
import { therapyCategoryService } from '@/services/therapyCategoryService'
import { normalizeOssUrlForPresign } from '@/utils/ossUrl'

export default {
  name: 'TherapyActivityManagement',
  data() {
    return {
      list: [],
      form: this.getInitialForm(),
      filters: { status: '' },
      statusMap: { DRAFT: '草稿', PUBLISHED: '已发布', OFFLINE: '已下架' },
      gradeInputText: '',
      gradeRows: [],
      skuModeEnabled: false,
      skuRows: [],
      page: 0,
      size: 10,
      totalPages: 1,
      showDialog: false,
      editingId: null,
      loading: false,
      loadingField: '',
      previewUrls: {},
      categoryOptions: [],
      categoryList: [],
      showCategoryDialog: false,
      showCategoryEditDialog: false,
      editingCategoryId: null,
      categoryForm: { name: '', sortOrder: 0, status: 'ACTIVE' },
      sectionTypeLabels: {
        text: '纯文本',
        numberedList: '编号列表',
        bulletList: '圆点列表',
        highlight: '重点提示'
      },
      showDeactivateModal: false,
      deactivateModalMode: 'confirm',
      deactivateTarget: null,
      deactivateCheck: null,
      bookingStatusLabels: {
        PENDING_PAYMENT: '待付款',
        PAID: '已付款',
        CONFIRMED: '已确认',
        CHECKED_IN: '已到店'
      }
    }
  },
  mounted() {
    this.loadData()
    this.loadCategories()
  },
  methods: {
    getInitialForm() {
      return {
        name: '', durationMinutes: 60, price: 0, originalPrice: 0, sortOrder: 0,
        summary: '', serviceAddress: '', contentSections: [], status: 'DRAFT',
        coverImageUrl: '', carouselImages: [], detailImages: [],
        categoryIds: []
      }
    },
    addSection(type) {
      const section = { title: '', type }
      if (type === 'text') {
        section.content = ''
      } else if (type === 'numberedList') {
        section.items = [{ title: '', description: '' }]
      } else if (type === 'bulletList') {
        section.items = [{ title: '', description: '' }]
      } else if (type === 'highlight') {
        section.items = [{ title: '', description: '' }]
      }
      this.form.contentSections.push(section)
    },
    removeSection(idx) {
      this.form.contentSections.splice(idx, 1)
    },
    moveSection(idx, delta) {
      const arr = this.form.contentSections
      const newIdx = idx + delta
      if (newIdx < 0 || newIdx >= arr.length) return
      const temp = arr[idx]
      arr.splice(idx, 1)
      arr.splice(newIdx, 0, temp)
      this.form.contentSections = [...arr]
    },
    addGradeFromInput() {
      const text = (this.gradeInputText || '').trim()
      if (!text) return
      text.split(/[,，]/).map(s => s.trim()).filter(s => s).forEach(grade => {
        if (!this.gradeRows.find(r => r.grade === grade)) {
          this.gradeRows.push({ grade, price: null, originalPrice: null })
        }
      })
      this.gradeInputText = ''
      this.syncAllSkuGradeRows()
    },
    removeGradeRow(idx) {
      this.gradeRows.splice(idx, 1)
      this.syncAllSkuGradeRows()
    },
    syncGradeToForm() {
      this.form.gradeOptions = this.gradeRows.map(r => r.grade)
      this.form.gradePrices = this.gradeRows.map((r, idx) => ({
        grade: r.grade,
        price: idx === 0 ? 0 : r.price,
        originalPrice: idx === 0 ? null : r.originalPrice
      }))
    },
    enableSkuMode() {
      this.skuModeEnabled = true
      if (!this.skuRows.length) this.addSkuRow()
    },
    disableSkuMode() {
      this.skuModeEnabled = false
      this.skuRows = []
    },
    buildSkuGradeRows() {
      return []
    },
    syncAllSkuGradeRows() {
      /* SKU 不再配置等级价，保留空方法避免旧调用报错 */
    },
    addSkuRow() {
      this.skuRows.push({
        name: '',
        price: null,
        originalPrice: null,
        durationMinutes: null,
        sortOrder: this.skuRows.length,
        enabled: true
      })
    },
    removeSkuRow(idx) {
      this.skuRows.splice(idx, 1)
    },
    syncSkuToForm() {
      if (!this.skuModeEnabled || !this.skuRows.length) {
        this.form.skus = []
        return
      }
      this.form.skus = this.skuRows.map(r => ({
        id: r.id || undefined,
        name: r.name,
        price: r.price,
        originalPrice: r.originalPrice,
        durationMinutes: r.durationMinutes,
        sortOrder: r.sortOrder,
        enabled: r.enabled !== false
      }))
    },
    loadSkuRowsFromItem(item) {
      const skus = item.skus || []
      this.skuModeEnabled = skus.length > 0
      this.skuRows = skus.map(s => ({
        id: s.id,
        name: s.name,
        price: s.price,
        originalPrice: s.originalPrice,
        durationMinutes: s.durationMinutes,
        sortOrder: s.sortOrder ?? 0,
        enabled: s.enabled !== false
      }))
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapyActivityService.fetchList({ status: this.filters.status, page: this.page, size: this.size })
        this.list = res.data?.content || []
        this.totalPages = res.data?.totalPages || 1
      } catch (e) {
        alert(e?.response?.data?.error || '加载活动失败')
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const res = await therapyCategoryService.fetchList()
        this.categoryOptions = (res.data || []).filter(c => c.status === 'ACTIVE')
      } catch (e) {
        console.error('加载分类失败', e)
      }
    },
    async openCategoryDialog() {
      this.showCategoryDialog = true
      await this.loadCategoryList()
    },
    async loadCategoryList() {
      try {
        const res = await therapyCategoryService.fetchList()
        this.categoryList = res.data || []
      } catch (e) {
        alert(e?.response?.data?.error || '加载分类列表失败')
      }
    },
    openCreateCategory() {
      this.editingCategoryId = null
      this.categoryForm = { name: '', sortOrder: 0, status: 'ACTIVE' }
      this.showCategoryEditDialog = true
    },
    openEditCategory(cat) {
      this.editingCategoryId = cat.id
      this.categoryForm = { name: cat.name, sortOrder: cat.sortOrder, status: cat.status }
      this.showCategoryEditDialog = true
    },
    async saveCategory() {
      if (!this.categoryForm.name.trim()) { alert('请输入分类名称'); return }
      this.loading = true
      try {
        if (this.editingCategoryId) await therapyCategoryService.update(this.editingCategoryId, this.categoryForm)
        else await therapyCategoryService.create(this.categoryForm)
        this.showCategoryEditDialog = false
        await this.loadCategoryList()
        await this.loadCategories()
      } catch (e) {
        alert(e?.response?.data?.error || '保存分类失败')
      } finally {
        this.loading = false
      }
    },
    async toggleCategoryStatus(cat) {
      if (!window.confirm(`确认${cat.status === 'ACTIVE' ? '禁用' : '启用'}"${cat.name}"吗？`)) return
      const status = cat.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      this.loading = true
      try {
        await therapyCategoryService.updateStatus(cat.id, status)
        await this.loadCategoryList()
        await this.loadCategories()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingId = null
      this.form = this.getInitialForm()
      this.gradeRows = []
      this.gradeInputText = ''
      this.skuModeEnabled = false
      this.skuRows = []
      this.showDialog = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.form = { 
        ...item,
        categoryIds: (item.categories || []).map(c => c.id),
        carouselImages: item.carouselImages || [],
        detailImages: item.detailImages || [],
        contentSections: (item.contentSections || []).map(s => {
          const copy = { ...s }
          if (copy.items) copy.items = copy.items.map(it => ({ ...it }))
          return copy
        })
      }
      const opts = item.gradeOptions || []
      const prices = item.gradePrices || []
      this.gradeRows = opts.map((g, idx) => {
        const gp = prices.find(p => p.grade === g)
        return {
          grade: g,
          price: idx === 0 ? 0 : (gp?.price ?? null),
          originalPrice: idx === 0 ? null : (gp?.originalPrice ?? null)
        }
      })
      this.gradeInputText = ''
      this.loadSkuRowsFromItem(item)
      this.showDialog = true
    },
    async save() {
      if (!this.form.name) return
      this.syncGradeToForm()
      this.syncSkuToForm()
      this.loading = true
      try {
        if (this.editingId) await therapyActivityService.update(this.editingId, this.form)
        else await therapyActivityService.create(this.form)
        this.showDialog = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '保存活动失败')
      } finally {
        this.loading = false
      }
    },
    async changeStatus(item, status) {
      if (status === 'OFFLINE') {
        await this.openDeactivateModal(item, status)
        return
      }
      if (!window.confirm(`确认将活动"${item.name}"发布吗？`)) return
      this.loading = true
      try {
        await therapyActivityService.updateStatus(item.id, status)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    async openDeactivateModal(item, status) {
      this.loading = true
      try {
        const res = await therapyActivityService.checkDeactivate(item.id)
        const check = res.data || {}
        this.deactivateTarget = { item, status }
        this.deactivateCheck = check
        this.deactivateModalMode = check.hasActiveBookings ? 'blocked' : 'confirm'
        this.showDeactivateModal = true
      } catch (e) {
        alert(e?.response?.data?.error || '检查预约状态失败')
      } finally {
        this.loading = false
      }
    },
    closeDeactivateModal() {
      this.showDeactivateModal = false
      this.deactivateTarget = null
      this.deactivateCheck = null
    },
    async confirmDeactivate() {
      if (!this.deactivateTarget) return
      const { item, status } = this.deactivateTarget
      this.loading = true
      try {
        await therapyActivityService.updateStatus(item.id, status)
        this.closeDeactivateModal()
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    formatBookingStatusBreakdown(breakdown) {
      if (!breakdown) return ''
      return Object.entries(breakdown)
        .map(([status, count]) => `${this.bookingStatusLabels[status] || status} ${count} 笔`)
        .join('、')
    },
    changePage(p) {
      this.page = p
      this.loadData()
    },
    async uploadImage(event, field) {
      const file = event.target.files[0]
      if (!file) return
      this.loadingField = field
      try {
        const res = await therapyActivityService.uploadImage(file)
        if (res.success) {
          this.form[field] = res.data.url
        }
      } catch (e) {
        alert(e?.response?.data?.message || '上传失败')
      } finally {
        this.loadingField = ''
        event.target.value = ''
      }
    },
    async uploadMultipleImages(event, field) {
      const files = Array.from(event.target.files)
      if (!files.length) return
      this.loadingField = field
      try {
        for (const file of files) {
          const res = await therapyActivityService.uploadImage(file)
          if (res.success) {
            if (!this.form[field]) this.form[field] = []
            this.form[field].push(res.data.url)
          }
        }
      } catch (e) {
        alert(e?.response?.data?.message || '上传失败')
      } finally {
        this.loadingField = ''
        event.target.value = ''
      }
    },
    getPreviewUrl(url) {
      if (!url) return ''
      if (url.startsWith('data:')) return url
      const cacheKey = normalizeOssUrlForPresign(url)
      if (this.previewUrls[cacheKey]) return this.previewUrls[cacheKey]

      if (url.includes('aliyuncs.com')) {
        therapyActivityService.getPresignedUrl(url).then(res => {
          const signed = res.data?.url || res.url
          if (signed) {
            this.previewUrls = { ...this.previewUrls, [cacheKey]: signed }
          }
        }).catch(err => {
          console.error('获取预签名URL失败:', err)
          this.previewUrls = { ...this.previewUrls, [cacheKey]: url }
        })
        return ''
      }
      
      if (url.startsWith('http')) return url
      return ''
    }
  }
}
</script>

<style scoped>
.toolbar select,
.toolbar input {
  height: 40px;
}
.toolbar select {
  min-width: 120px;
}
.section-fieldset {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
}
.section-fieldset legend {
  font-weight: 600;
  font-size: 1rem;
  color: #374151;
  padding: 0 8px;
}
.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.form-col {
  flex: 1;
  min-width: 160px;
}
.field-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}
.field-label {
  font-weight: 600;
  color: #111827;
  font-size: 0.9375rem;
}
.field-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
}
.block-hint {
  display: block;
  margin-bottom: 2px;
}
.image-spec-banner {
  margin: 0 0 12px;
  padding: 10px 12px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #4b5563;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}
.image-spec-banner strong,
.field-hint strong {
  color: #1e40af;
  font-weight: 600;
}
.form-group .field-block input {
  width: 100%;
  box-sizing: border-box;
}
.form-group label.full-width {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}
.form-group label {
  font-weight: 500;
  color: #333;
}
.grade-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.grade-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 0.85rem;
  color: #166534;
}
.chip-remove {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  margin-left: 2px;
}
.grade-price-table-wrap {
  margin-top: 10px;
  overflow-x: auto;
}
.grade-price-table {
  width: 100%;
  border-collapse: collapse;
}
.grade-price-table th,
.grade-price-table td {
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  text-align: left;
  font-size: 0.875rem;
}
.grade-price-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}
.gp-input {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  box-sizing: border-box;
}
.grade-price-tag {
  display: inline-block;
  font-size: 0.75rem;
  color: #166534;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: 1px 8px;
  border-radius: 4px;
  margin-right: 4px;
}
.sku-enable-bar {
  margin: 12px 0;
}
.sku-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}
.sku-table-wrap {
  overflow-x: auto;
}
.sku-grade-panel {
  margin: 8px 0 12px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.sku-grade-title {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 0.875rem;
}
.sku-grade-btn {
  font-size: 0.8rem;
  padding: 2px 8px;
}
.category-tag {
  display: inline-block;
  font-size: 0.75rem;
  color: #1e40af;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 1px 8px;
  border-radius: 4px;
  margin-right: 2px;
}
.category-checklist {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.category-check-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}
.category-check-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
}
.image-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.image-preview-container {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  overflow: hidden;
  position: relative;
}
.image-preview {
  max-width: 100%;
  max-height: 400px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}
.image-placeholder {
  color: #9ca3af;
  font-size: 0.9rem;
  padding: 2rem;
}
.image-upload-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.upload-status {
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 500;
}
.ghost-button {
  background: transparent;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.danger-link {
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  width: 100%;
}
.grid-item {
  min-height: 150px;
}
.grid-item .image-preview {
  max-height: 150px;
}
.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}
.remove-btn:hover {
  background: #dc2626;
}
.dialog-wide {
  max-width: 800px;
  width: 95vw;
}
.deactivate-summary p {
  margin: 0 0 8px;
  color: #374151;
}
.deactivate-alert {
  border-radius: 8px;
  padding: 14px 16px;
  line-height: 1.6;
}
.deactivate-alert p {
  margin: 0 0 8px;
}
.deactivate-alert p:last-child {
  margin-bottom: 0;
}
.deactivate-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}
.deactivate-alert-warning {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
}
.deactivate-hint {
  font-size: 0.875rem;
  color: #b91c1c;
}
.deactivate-confirm-btn {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  cursor: pointer;
}
.deactivate-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.dialog-cancel-btn {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  cursor: pointer;
}

/* ── 内容板块编辑器 ── */
.cs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.cs-hint {
  font-size: 0.8125rem;
  color: #6b7280;
}
.cs-add-btns {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.cs-add-btn {
  font-size: 0.8125rem;
  padding: 0.3rem 0.7rem;
  white-space: nowrap;
}
.cs-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  padding: 16px 0;
}
.cs-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #fff;
}
.cs-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.cs-title-input {
  flex: 1;
  font-weight: 600;
  font-size: 0.9375rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
}
.cs-type-badge {
  font-size: 0.75rem;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}
.cs-card-actions {
  display: flex;
  gap: 2px;
  margin-left: auto;
}
.cs-action-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 2px 8px;
  color: #374151;
  line-height: 1.2;
}
.cs-action-btn:hover {
  background: #f3f4f6;
}
.cs-remove-btn {
  color: #ef4444;
  border-color: #fecaca;
}
.cs-remove-btn:hover {
  background: #fef2f2;
}
.cs-card-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cs-item-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cs-item-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1e40af;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}
.cs-item-bullet {
  font-size: 1.2rem;
  color: #374151;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
}
.cs-item-title {
  width: 140px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.cs-item-desc {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.85rem;
  min-width: 0;
}
.cs-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;
}
.cs-highlight-body {
  background: #fffbeb;
  border-radius: 6px;
  padding: 8px;
  border-left: 3px solid #f59e0b;
}
.category-toolbar {
  margin-bottom: 12px;
}
.category-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}
.category-table th,
.category-table td {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
  font-size: 0.875rem;
}
.category-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}
</style>